// Environment Configuration Logic

import {
  detectAppEnvironment,
  getTenantSubdomain,
  isGenericCompanyPortalHost,
  parseTenantSubdomain,
} from "./utils/subdomain";

interface Config {
  GRAPHQL_URI: string;
  SOCKET_URI: string;
  APP_URI: string;
}

const configs: Record<string, Config> = {
  local: {
    GRAPHQL_URI: "http://localhost:4000/graphql",
    SOCKET_URI: "ws://localhost:5010/ws/onboarding",
    APP_URI: "http://localhost:3005/app",
  },
  development: {
    GRAPHQL_URI: "https://company-dev.criticalasset.com/graphql",
    SOCKET_URI: "wss://ai-dev.criticalasset.com/ws/onboarding",
    APP_URI: "https://company-dev.criticalasset.com/app",
  },
  staging: {
    GRAPHQL_URI: "https://company-stg.criticalasset.com/graphql",
    SOCKET_URI: "wss://ai-stg.criticalasset.com/ws/onboarding",
    APP_URI: "https://company-stg.criticalasset.com/app",
  },
  production: {
    GRAPHQL_URI: "https://beta.criticalasset.com/graphql",
    SOCKET_URI: "wss://ai.criticalasset.com/ws/onboarding",
    APP_URI: "https://beta.criticalasset.com/app",
  },
};

const socketByEnv: Record<string, string> = {
  local: configs.local.SOCKET_URI,
  development: configs.development.SOCKET_URI,
  staging: configs.staging.SOCKET_URI,
  production: configs.production.SOCKET_URI,
};

const isLocalDevHostname = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.endsWith(".localhost") ||
  hostname.startsWith("192.168.") ||
  hostname.startsWith("10.") ||
  // Explicit escape hatch for tunneled local dev (e.g. cloudflared/ngrok quick tunnels used to
  // test against claude.ai, which can't reach localhost at all). Hostname-based detection alone
  // can't recognize a random *.trycloudflare.com/*.ngrok-free.app URL as "local" -- without this
  // it silently falls through to the production GRAPHQL_URI/SOCKET_URI, pointing a local dev
  // session at the real prod backend. Only set VITE_FORCE_LOCAL_ENV when intentionally tunneling.
  import.meta.env.VITE_FORCE_LOCAL_ENV === "true";

const getConfig = (): Config => {
  if (typeof window === "undefined") {
    return configs.production;
  }

  const hostname = window.location.hostname;
  const env = detectAppEnvironment(hostname);
  const tenantSlug = parseTenantSubdomain(hostname);

  // Local dev: API/auth on localhost ports; UI can be gems.localhost:3005 etc.
  if (isLocalDevHostname(hostname) || env === "local") {
    // Tunnel testing (cloudflared/ngrok, e.g. for claude.ai web) serves this page over HTTPS.
    // Browsers block HTTPS pages from fetching plain http:// (mixed content) -- silently, as a
    // generic "NetworkError when attempting to fetch resource", not a CORS-looking error. Set
    // VITE_TUNNEL_API_BASE to the graphql-service tunnel's HTTPS URL to route API/WS calls
    // through it instead of local plain-HTTP localhost:4000.
    const tunnelApiBase = import.meta.env.VITE_TUNNEL_API_BASE as string | undefined;
    const apiHost = "localhost";
    const localBase: Config = tunnelApiBase
      ? {
          GRAPHQL_URI: `${tunnelApiBase}/graphql`,
          SOCKET_URI: `${tunnelApiBase.replace(/^http/, "ws")}/ws/onboarding`,
          APP_URI: tunnelApiBase,
        }
      : {
          GRAPHQL_URI: `http://${apiHost}:4000/graphql`,
          SOCKET_URI: `ws://${apiHost}:5010/ws/onboarding`,
          APP_URI: 'http://localhost:4000',
        };
    if (tenantSlug && !isGenericCompanyPortalHost(hostname)) {
      return localBase;
    }
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return {
        ...localBase,
        APP_URI: `http://${hostname}:3005`,
      };
    }
    return localBase;
  }

  // Deployed tenant subdomain: API on same origin (nginx proxies /graphql)
  if (tenantSlug && !isGenericCompanyPortalHost(hostname)) {
    const origin = window.location.origin;
    return {
      GRAPHQL_URI: `${origin}/graphql`,
      SOCKET_URI: socketByEnv[env] ?? configs.production.SOCKET_URI,
      APP_URI: `${origin}/app`,
    };
  }

  if (env === "development") return configs.development;
  if (env === "staging") return configs.staging;
  return configs.production;
};

const currentConfig = getConfig();

export const GRAPHQL_URI = currentConfig.GRAPHQL_URI;
export const SOCKET_URI = currentConfig.SOCKET_URI;
export const APP_URI = currentConfig.APP_URI;

export { getTenantSubdomain, isGenericCompanyPortalHost as isGenericPortal };

const adminLoginByEnv: Record<string, string> = {
  local: "http://localhost:3000/login",
  development: "https://admin-dev.criticalasset.com/login",
  staging: "https://admin-stg.criticalasset.com/login",
  production: "https://admin.criticalasset.com/login",
};

/** Admin SPA login URL (Super Admin / Partner accounts). */
export function getAdminLoginUrl(): string {
  if (typeof window === "undefined") {
    return adminLoginByEnv.production;
  }
  const env = detectAppEnvironment(window.location.hostname);
  return adminLoginByEnv[env] ?? adminLoginByEnv.production;
}

const isLocalApiUrl = (hostname: string): boolean =>
  hostname === "localhost" || hostname === "127.0.0.1";

/**
 * GraphQL endpoint for application OAuth (client credentials + refresh).
 * Use mutations `applicationClientCredentialsToken` and `applicationRefreshToken`.
 */
export function getDeveloperApiGraphqlUrl(): string {
  return GRAPHQL_URI;
}

/** @deprecated Use {@link getDeveloperApiGraphqlUrl} with `applicationClientCredentialsToken`. */
export function getAuthOAuthTokenUrl(): string {
  return getDeveloperApiGraphqlUrl();
}

/** @deprecated Use {@link getDeveloperApiGraphqlUrl} with `applicationRefreshToken`. */
export function getAuthOAuthRefreshUrl(): string {
  return getDeveloperApiGraphqlUrl();
}

/** HTTP base for REST routes co-hosted with GraphQL (e.g. document stream proxy). */
export function getGraphqlApiBaseUrl(): string {
  try {
    const u = new URL(GRAPHQL_URI);
    let pathname = u.pathname.replace(/\/$/, "");
    if (pathname.toLowerCase().endsWith("/graphql")) {
      pathname = pathname.slice(0, -"/graphql".length);
    } else if (pathname.toLowerCase().endsWith("/api")) {
      pathname = pathname.slice(0, -"/api".length) + "/app";
    }
    u.pathname = pathname || "/";
    u.hash = "";
    u.search = "";
    return u.toString().replace(/\/$/, "");
  } catch {
    return GRAPHQL_URI
      .replace(/\/graphql\/?$/i, "")
      .replace(/\/api\/?$/i, "/app")
      .replace(/\/$/, "");
  }
}

/**
 * Auth service base for validate-subdomain / resolve-login (same host as API in prod).
 *
 * NOTE: this returns a bare origin (no /app or /api path), which is wrong for reaching any real
 * backend route on a deployed tenant subdomain -- nginx there only proxies specific prefixes
 * (/app -> graphql-service) to the backend; a bare path falls through to the SPA's own static
 * index.html instead. Confirmed live. Left as-is because its one remaining caller
 * (TenantGate.tsx) only checks `res.ok`, and the SPA shell's 200 response happens to satisfy
 * that check -- fixing this path would turn that accidental pass into a hard 404, which
 * TenantGate treats as "Company not found" and blocks the whole app. Real backend calls (MCP
 * OAuth resume in Login.tsx, OAuthConsent.tsx) use getGraphqlApiBaseUrl() instead, which
 * resolves correctly. Fix this properly (add a real backend validate-subdomain route reachable
 * under /app, then switch TenantGate to it) rather than patching the symptom here.
 */
export function getAuthServiceBaseUrl(): string {
  if (typeof window !== "undefined" && isLocalDevHostname(window.location.hostname)) {
    return "http://localhost:3001";
  }
  try {
    const base = getGraphqlApiBaseUrl();
    const u = new URL(base);
    if (isLocalApiUrl(u.hostname)) {
      return `${u.protocol}//${u.hostname}:3001`;
    }
    return u.origin;
  } catch {
    return "";
  }
}

const cdnByEnv: Record<string, string> = {
  local: "dev-cdn.criticalasset.com",
  development: "dev-cdn.criticalasset.com",
  staging: "stg-cdn.criticalasset.com",
  production: "cdn.criticalasset.com",
};

export function getCdnUrl(): string {
  if (typeof window === "undefined") {
    return cdnByEnv.production;
  }
  const env = detectAppEnvironment(window.location.hostname);
  return cdnByEnv[env] ?? cdnByEnv.production;
}
